import { call, delay, put, takeLatest } from 'redux-saga/effects';
import { remittanceApi } from '../api/remittanceApi';
import { remittanceActions } from './remittanceSlice';
import { logApiErrorToSentry } from '../utils';
import { PayloadAction } from '@reduxjs/toolkit';
import { IRemittanceAPIProps } from '../api/IRemittanceAPI';

function* fetchPurposeList(): any {
  try {
    const result = yield call(remittanceApi.getPurposeList);
    yield put(remittanceActions.fetchPurposeListSucceeded(result?.data ?? []));
  } catch(e: any) {
    yield put(remittanceActions.fetchPurposeListFailed(e?.response?.data?.error?.type || e?.response?.error  || e?.message || 'API - Failed to load purpose list'));
    logApiErrorToSentry(e, "fetchPurposeList");
  }
}

function* fetchLinkedVendorCode(): any {
  try {
    const result = yield call(remittanceApi.getLinkedVendorCode);
    yield put(remittanceActions.fetchLinkedVendorCodeSucceeded(result?.data ?? []));
  } catch(e: any) {
    yield put(remittanceActions.fetchLinkedVendorCodeFailed(e?.response?.data?.error?.type || e?.response?.error  || e?.message || 'API - Failed to load vendor list'));
    logApiErrorToSentry(e, "fetchLinkedVendorCode");
  }
}

function* fetchCardRate(): any {
  try {
    const result = yield call(remittanceApi.getCardRate);
    yield delay(300);
    yield put(remittanceActions.fetchCardRateSucceeded(result?.data?.cardRate ?? []));
  } catch(e: any) {
    yield put(remittanceActions.fetchCardRateFailed(e?.response?.data?.error?.type || e?.response?.error  || e?.message || 'API - Failed to load card rates'));
    logApiErrorToSentry(e, "fetchCardRate");
  }
}

function* fetchFxRates(action: PayloadAction<string>): any {
  try {
    const result = yield call(remittanceApi.getFxRates, action.payload);
    yield delay(300);
    yield put(remittanceActions.fetchFxRatesSucceeded(result?.data ?? null));
  } catch(e: any) {
    yield put(remittanceActions.fetchFxRatesFailed(e?.response?.data?.error?.type || e?.response?.error  || e?.message || 'API - Failed to get fx rates'));
    logApiErrorToSentry(e, "fetchFxRates");
  }
}

function* fetchTransactionSummary(action: PayloadAction<any>): any {
  try {
    const result = yield call(remittanceApi.getTransactionSummary, action.payload);
    yield put(remittanceActions.fetchTransactionSummarySucceeded(result?.data ?? null));
  } catch(e: any) {
    yield put(remittanceActions.fetchTransactionSummaryFailed(e?.response?.data?.error ?? { code: 500, type: "Something went wrong, please refresh the page and try again!"}));
    logApiErrorToSentry(e, "fetchTransactionSummary");
  }
}

function* fetchPanDeclaration(action: PayloadAction<any>): any {
  try {
    const result = yield call(remittanceApi.getTransactionSummary, action.payload);
    yield put(remittanceActions.fetchPanDeclarationSucceeded(result?.data ?? null));
  } catch(e: any) {
    yield put(remittanceActions.fetchPanDeclarationFailed(e?.response?.data?.error ?? { code: 500, type: "Something went wrong, please refresh the page and try again!"}));
    logApiErrorToSentry(e, "fetchPanDeclaration");
  }
}

function* fetchRemitterList(action: PayloadAction<IRemittanceAPIProps.IRemitterListPayload>): any {
  try {
    const result = yield call(remittanceApi.getRemitterList, action.payload);
    yield put(remittanceActions.fetchRemitterListSucceeded(result?.data ?? null));
  } catch(e: any) {
    yield put(remittanceActions.fetchRemitterListFailed(e?.response?.data?.error?.type || e?.response?.error || 'API - Failed to load remitter list'));
    logApiErrorToSentry(e, "fetchRemitterList");
  }
}

function* fetchRecipientList(action: PayloadAction<IRemittanceAPIProps.IRecipientListPayload>): any {
  try {
    const result = yield call(remittanceApi.getRecipientsByRemitter, action.payload);
    yield put(remittanceActions.fetchRecipientListSucceeded(result?.data ?? null));
  } catch(e: any) {
    yield put(remittanceActions.fetchRecipientListFailed(e?.response?.data?.error?.type || e?.response?.error  || 'API - Failed to load recipient list'));
    logApiErrorToSentry(e, "fetchRecipientList");
  }
}

function* fetchDocumentsList(action: PayloadAction<IRemittanceAPIProps.IDocumentsListPayload>): any {
  try {
    const result = yield call(remittanceApi.getDocumentsList, action.payload);
    yield put(remittanceActions.fetchDocumentsListSucceeded(result?.data ?? []));
  } catch(e: any) {
    yield put(remittanceActions.fetchDocumentsListFailed(e?.response?.data?.error?.type || e?.response?.error  || e?.message || 'API - Failed to load documents list'));
    logApiErrorToSentry(e, "fetchDocumentsList");
  }
}

function* fetchLeadDocumentsList(action: PayloadAction<IRemittanceAPIProps.ILeadDocumentsListPayload>): any {
  try {
    const result = yield call(remittanceApi.getLeadDocumentsList, action.payload);
    yield put(remittanceActions.fetchLeadDocumentsListSucceeded(result?.data?.leadDocList ?? []));
  } catch(e: any) {
    yield put(remittanceActions.fetchLeadDocumentsListFailed(e?.response?.data?.error?.type || e?.response?.error  || e?.message || 'API - Failed to load lead documents list'));
    logApiErrorToSentry(e, "fetchLeadDocumentsList");
  }
}

function* fetchRemitterSignedStatus(action: PayloadAction<string>): any {
  try {
    const result = yield call(remittanceApi.getRemitterSignedStatus, action.payload);
    yield put(remittanceActions.fetchRemitterSignedStatusSucceeded(result?.data ?? null));
  } catch(e: any) {
    yield put(remittanceActions.fetchRemitterSignedStatusFailed(e?.response?.data?.error?.type || e?.response?.error  || e?.message || 'API - Failed to get signed values'));
    logApiErrorToSentry(e, "fetchRemitterSignedStatus");
  }
}

function* fetchGetModalContent(action: PayloadAction<string>): any {
  try {
    const result = yield call(remittanceApi.getModalContent, action.payload);
    yield put(remittanceActions.fetchGetModalContentSucceeded(JSON.parse(result?.data?.contentJson) ?? null));
  } catch(e: any) {
    yield put(remittanceActions.fetchGetModalContentFailed(e?.response?.data?.error?.type || e?.response?.error  || e?.message || 'API - Failed to get modal data'));
    logApiErrorToSentry(e, "fetchGetModalContent");
  }
}

function* fetchGetNeftRtgsBankDetails(action: PayloadAction<IRemittanceAPIProps.INeftRtgsPayload>): any {
  try {
    const result = yield call(remittanceApi.getNeftRtgsBankDetails, action.payload);
    yield put(remittanceActions.fetchGetNeftRtgsBankDetailsSucceeded(result?.data ?? null));
  } catch(e: any) {
    yield put(remittanceActions.fetchGetNeftRtgsBankDetailsFailed(e?.response?.data?.error?.type || e?.response?.error  || e?.message || 'API - Failed to get modal data'));
    logApiErrorToSentry(e, "fetchGetNeftRtgsBankDetails");
  }
}

function* fetchNetBankingList(): any {
  try {
    const result = yield call(remittanceApi.getNetBankingList);
    yield put(remittanceActions.fetchNetBankingListSucceeded(result?.data?.banks ?? []));
  } catch(e: any) {
    yield put(remittanceActions.fetchNetBankingListFailed(e?.response?.data?.error?.type || e?.response?.error  || e?.message || 'API - Failed to load purpose list'));
    logApiErrorToSentry(e, "fetchNetBankingList");
  }
}

function* fetchRecipientCategoryList(action: PayloadAction<IRemittanceAPIProps.IRecipientCategoryListParams>): any {
  try {
    const result = yield call(remittanceApi.getRecipientCategoryList, action.payload);
    yield put(remittanceActions.fetchRecipientCategoryListSucceeded(result?.data ?? []));
  } catch(e: any) {
    yield put(remittanceActions.fetchRecipientCategoryListFailed(e?.response?.data?.error?.type || e?.response?.error  || e?.message || 'API - Failed to load purpose list'));
    logApiErrorToSentry(e, "fetchRecipientCategoryList");
  }
}

export default function* remittanceSaga() {
  yield takeLatest(remittanceActions.fetchPurposeListRequested.type, fetchPurposeList)
  yield takeLatest(remittanceActions.fetchLinkedVendorCodeRequested.type, fetchLinkedVendorCode)
  yield takeLatest(remittanceActions.fetchCardRateRequested.type, fetchCardRate)
  yield takeLatest(remittanceActions.fetchFxRatesRequested.type, fetchFxRates)
  yield takeLatest(remittanceActions.fetchTransactionSummaryRequested.type, fetchTransactionSummary)
  yield takeLatest(remittanceActions.fetchPanDeclarationRequested.type, fetchPanDeclaration)
  yield takeLatest(remittanceActions.fetchRemitterListRequested.type, fetchRemitterList)
  yield takeLatest(remittanceActions.fetchRecipientListRequested.type, fetchRecipientList)
  yield takeLatest(remittanceActions.fetchDocumentsListRequested.type, fetchDocumentsList)
  yield takeLatest(remittanceActions.fetchLeadDocumentsListRequested.type, fetchLeadDocumentsList)
  yield takeLatest(remittanceActions.fetchRemitterSignedStatusRequested.type, fetchRemitterSignedStatus)
  yield takeLatest(remittanceActions.fetchGetModalContentRequested.type, fetchGetModalContent)
  yield takeLatest(remittanceActions.fetchGetNeftRtgsBankDetailsRequested.type, fetchGetNeftRtgsBankDetails)
  yield takeLatest(remittanceActions.fetchNetBankingListRequested.type, fetchNetBankingList)
  yield takeLatest(remittanceActions.fetchRecipientCategoryListRequested.type, fetchRecipientCategoryList)
}